// src/rms/pages/SettingsAgreements.jsx
// ============================================================================
// Settings > Agreements
// ============================================================================
// Where an operator rewrites the contract their customers sign.
//
// Until this screen existed the only contract prose in the product was a
// hardcoded string in CheckoutFlow.jsx followed by a note telling the operator
// to replace it with their own counsel-approved text -- with no way to do so.
//
// ----------------------------------------------------------------------------
// A plain textarea, deliberately
// ----------------------------------------------------------------------------
// No rich editor, no formatting toolbar. The stored body is exactly the text
// that gets signed, in the small grammar render.js understands, and what the
// owner types here is what the customer reads. Anything that produced markup
// would be a way for the read-only page and the PDF to disagree about the same
// words -- and on a contract that is not a rendering bug, it is an evidentiary
// one.
//
// The whole page sits behind <PinGate section="settings"> at the route, so
// capabilities are read from useElevation() rather than useRms(): on a shared
// counter the session is staff all day and the person who just typed their PIN
// is the owner.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { useElevation } from "../components/PinGate";
import PinModal from "../components/PinModal";
import { Field, TextInput, Banner, formatDate } from "../components/ui";
import agreementService from "../data/agreementService";
import { AGREEMENT_TOKENS, unknownTokens } from "../agreements/tokens";
import { toBlocks, wordCount } from "../agreements/render";
import { LIBRARY_VERSION } from "../agreements/library";
import { SUPPORTED_LOCALES } from "../i18n/locale";

/** A language is named in its own language, which is why this reads locale.js
 *  rather than the catalogues: "Français" is right on an English screen and
 *  "French" would be wrong on a French one. SUPPORTED_LOCALES already carries
 *  the endonym for all thirteen. */
const languageName = (code) =>
  SUPPORTED_LOCALES.find((l) => l.code === String(code || "").toLowerCase().split(/[-_]/)[0])?.endonym
  || code;

export default function SettingsAgreements({ operator, setNotice }) {
  const { t, member, locale } = useRms();
  const elevation = useElevation();

  const [rows, setRows] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  // "save" | "reset" | null -- which action the PIN box is about to authorise.
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    const result = await agreementService.listTemplates(operator.id);
    if (!result.success) {
      setProblem(result.error);
      setRows([]);
      return;
    }
    setRows(result.data || []);
  }, [operator.id]);

  // Install on arrival rather than at provisioning. seedOperator returns early
  // once any asset exists, so hooking it there would leave every business
  // created before this shipped with an empty library and no way to fill it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const installed = await agreementService.ensureTemplates(operator);
      if (cancelled) return;
      if (!installed.success) setProblem(installed.error);
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [operator, load]);

  const documents = useMemo(() => agreementService.groupByDocument(rows || []), [rows]);
  const selected = useMemo(
    () => (rows || []).find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  // Choose something as soon as there is something to choose.
  useEffect(() => {
    if (selectedId || !rows?.length) return;
    const preferred =
      rows.find((r) => r.doc_key === "rental_agreement" && r.locale.startsWith(String(locale || "en").slice(0, 2))) ||
      rows.find((r) => r.doc_key === "rental_agreement") ||
      rows[0];
    setSelectedId(preferred.id);
  }, [rows, selectedId, locale]);

  // Load the chosen document into the form. Keyed on the row's own updated_at
  // as well as its id, so a save refreshes the fields instead of leaving the
  // pre-save text sitting in them.
  useEffect(() => {
    if (!selected) return;
    setTitle(selected.title || "");
    setBody(selected.body || "");
    setPreview(false);
    setProblem(null);
  }, [selected?.id, selected?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!selected && (title !== selected.title || body !== selected.body);
  const badTokens = unknownTokens(body);
  const blocks = useMemo(() => (preview ? toBlocks(body) : []), [preview, body]);
  const words = wordCount(body);
  const outdated =
    selected && selected.library_version > 0 && selected.library_version < LIBRARY_VERSION;

  const memberId = elevation.member || member?.id || null;

  // Written out rather than looked up in a table of [key, fallback] pairs,
  // because verify-i18n finds a key only when t() is called with a literal --
  // a table would leave these five permanently English and invisible to the
  // twelve catalogues, which is worse than the repetition.
  const docLabel = (docKey, fallbackTitle) => {
    if (docKey === "rental_agreement") return t("agreements.doc.rentalAgreement", "Rental agreement");
    if (docKey === "liability_waiver") return t("agreements.doc.liabilityWaiver", "Liability waiver");
    if (docKey === "additional_driver") return t("agreements.doc.additionalDriver", "Additional driver");
    if (docKey === "lien_notice") return t("agreements.doc.lienNotice", "Lien notice");
    if (docKey === "certification_ack") return t("agreements.doc.certificationAck", "Certification acknowledgement");
    // An operator's own document keeps whatever they called it.
    return fallbackTitle || docKey;
  };

  // --- writes ---------------------------------------------------------------

  const runSave = async (pin) => {
    const result = await agreementService.saveTemplate(operator, selected.id, {
      pin,
      title,
      body,
      memberId,
    });
    if (!result.success) return { error: result.error };
    setPending(null);
    setNotice(t("agreements.saved", "Saved. New documents will use this wording; anything already signed keeps the wording it was signed with."));
    await load();
    return {};
  };

  const runReset = async (pin) => {
    const result = await agreementService.resetTemplate(operator, selected, { pin, memberId });
    if (!result.success) return { error: result.error };
    setPending(null);
    setNotice(t("agreements.reset", "Put back to the standard wording."));
    await load();
    return {};
  };

  const toggleActive = async () => {
    setBusy(true);
    const result = await agreementService.setActive(selected.id, !selected.is_active);
    setBusy(false);
    if (!result.success) {
      setNotice({ tone: "danger", text: result.error });
      return;
    }
    await load();
  };

  const startSave = () => {
    const invalid = agreementService.validateBody(body);
    if (invalid) {
      setProblem(invalid);
      return;
    }
    setProblem(null);
    setPending("save");
  };

  // --- render ---------------------------------------------------------------

  if (rows === null) {
    return <p style={{ color: "var(--rms-muted-strong)" }}>{t("common.loading", "Loading…")}</p>;
  }

  return (
    <>
      {/* Said first, and not in a tooltip. Somebody about to send this to a
          customer should read it before they read the contract. */}
      <Banner tone="warn">
        {t(
          "agreements.notAdvice",
          "These are starting points, not legal advice. Nobody's lawyer has read them. Check every document against the law where you trade — and against how you actually run your business — before you send one to a customer."
        )}
      </Banner>

      <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0, maxWidth: 640 }}>
        {t(
          "agreements.intro",
          "Your wording, kept for your business alone. Editing these changes nothing for anyone else, and never changes a document a customer has already signed."
        )}
      </p>

      {problem ? <Banner tone="danger">{problem}</Banner> : null}

      {!rows.length ? (
        <div className="rms-card rms-card-pad">
          <p style={{ margin: 0, fontSize: 13.5 }}>
            {t("agreements.none", "There are no starter documents for this trade yet.")}
          </p>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* The library */}
        <div style={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
          {documents.map((doc) => (
            <div key={doc.docKey} style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--rms-muted)",
                  marginBottom: 6,
                }}
              >
                {docLabel(doc.docKey, doc.locales[0]?.title)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {doc.locales.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className={`rms-btn rms-btn--sm ${row.id === selectedId ? "rms-btn--primary" : "rms-btn--ghost"}`}
                    onClick={() => setSelectedId(row.id)}
                  >
                    {languageName(row.locale)}
                    {row.is_active ? "" : ` · ${t("agreements.off", "off")}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* The document */}
        {selected ? (
          <div style={{ flex: "3 1 420px", minWidth: 320 }}>
            {!selected.reviewed_at ? (
              <Banner tone="info">
                {t(
                  "agreements.unreviewed",
                  "You have not edited this one yet — it is still the standard wording exactly as it shipped."
                )}
              </Banner>
            ) : null}

            {outdated ? (
              <Banner tone="info">
                {t(
                  "agreements.outdated",
                  "The standard version of this document has been revised since yours was created. Yours is untouched — use “Back to standard wording” if you want the new text, but you will lose your own edits."
                )}
              </Banner>
            ) : null}

            <Field label={t("agreements.title", "Document title")} required>
              <TextInput value={title} onChange={setTitle} />
            </Field>

            <Field
              label={t("agreements.body", "Wording")}
              help={t(
                "agreements.bodyHelp",
                "Start a heading with ## and a bullet with a dash. Blank lines separate paragraphs. Everything else is printed exactly as you type it."
              )}
              required
            >
              {preview ? (
                <div
                  className="rms-card rms-card-pad"
                  style={{ maxHeight: 520, overflowY: "auto", background: "var(--rms-surface)" }}
                >
                  {blocks.map((block, i) => {
                    if (block.type === "heading") {
                      return (
                        <h3 key={i} style={{ fontSize: 14, margin: "18px 0 6px" }}>
                          {block.text}
                        </h3>
                      );
                    }
                    if (block.type === "list") {
                      return (
                        <ul key={i} style={{ fontSize: 13.5, lineHeight: 1.6, paddingLeft: 20, margin: "6px 0" }}>
                          {block.items.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={i} style={{ fontSize: 13.5, lineHeight: 1.6, margin: "6px 0" }}>
                        {block.text}
                      </p>
                    );
                  })}
                </div>
              ) : (
                <textarea
                  className="rms-textarea"
                  rows={22}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  spellCheck
                />
              )}
            </Field>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
                fontSize: 12,
                color: "var(--rms-muted-strong)",
                marginBottom: 12,
              }}
            >
              <span>{t("agreements.wordCount", "{count, plural, one {# word} other {# words}}", { count: words })}</span>
              {selected.reviewed_at ? (
                <span>
                  {t("agreements.lastEdited", "Last edited {when}", {
                    when: formatDate(selected.reviewed_at),
                  })}
                </span>
              ) : null}
            </div>

            {badTokens.length ? (
              <Banner tone="warn">
                {t(
                  "agreements.badTokens",
                  "This app cannot fill {list}. Placeholders it does not know are printed exactly as written on the document the customer signs.",
                  { list: badTokens.map((x) => `{{${x}}}`).join(", ") }
                )}
              </Banner>
            ) : null}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                onClick={startSave}
                disabled={!dirty || busy}
              >
                {t("agreements.save", "Save with my PIN")}
              </button>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setPreview((p) => !p)}>
                {preview ? t("agreements.edit", "Back to editing") : t("agreements.preview", "Preview")}
              </button>
              <button
                type="button"
                className="rms-btn rms-btn--ghost"
                onClick={() => setPending("reset")}
                disabled={busy || selected.library_version === 0}
              >
                {t("agreements.resetAction", "Back to standard wording")}
              </button>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={toggleActive} disabled={busy}>
                {selected.is_active
                  ? t("agreements.turnOff", "Turn this document off")
                  : t("agreements.turnOn", "Turn this document on")}
              </button>
            </div>

            {/* The palette. Nobody should have to guess a token name, and a
                guessed one prints through onto the signed document. */}
            <details>
              <summary style={{ fontSize: 13, cursor: "pointer", color: "var(--rms-muted-strong)" }}>
                {t("agreements.tokens", "Placeholders you can use")}
              </summary>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6, marginTop: 10 }}>
                {AGREEMENT_TOKENS.map((token) => (
                  <div key={token.key} style={{ fontSize: 12, color: "var(--rms-muted-strong)" }}>
                    <code style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                      {`{{${token.key}}}`}
                    </code>
                    <div>{token.hint}</div>
                  </div>
                ))}
              </div>
            </details>
          </div>
        ) : null}
      </div>

      {pending ? (
        <PinModal
          title={
            pending === "reset"
              ? t("agreements.pinResetTitle", "Put back the standard wording?")
              : t("agreements.pinSaveTitle", "Who is changing the contract?")
          }
          explain={t(
            "agreements.pinHelp",
            "Your PIN goes on the record beside this change, so the log can say who rewrote it and when."
          )}
          onCancel={() => setPending(null)}
          onResolved={pending === "reset" ? runReset : runSave}
        />
      ) : null}
    </>
  );
}
